import { Search, SlidersHorizontal } from "lucide-react";
import useSWR from "swr";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/features/authentication";
import { visionService } from "@/services/vision-service";
import { useProposalList } from "@/features/vision-360/proposal-management/list/context/use-proposal-list";
import { DateRangePicker } from "../../components/date-range-picker";

interface ProposalFiltersProps {
  showAdvanced: boolean;
  onToggleAdvanced: () => void;
}

export function ProposalFilters({
  showAdvanced,
  onToggleAdvanced,
}: ProposalFiltersProps) {
  const { filters, updateFilters } = useProposalList();
  const { userInfo } = useAuth();
  const userId = String(userInfo?.user?.id ?? "");

  const { data: executivesData } = useSWR(
    "proposal-executives",
    () => visionService.listExecutives(),
    { revalidateOnFocus: false },
  );
  const executives = executivesData?.executives ?? [];

  const selectValue = filters.created_by_user_id
    ? filters.created_by_user_id === userId
      ? "mine"
      : `exec:${filters.created_by_user_id}`
    : "all";

  function handleSelectChange(v: string) {
    if (v === "all") {
      updateFilters({ created_by_user_id: "" });
    } else if (v === "mine") {
      updateFilters({ created_by_user_id: userId });
    } else if (v.startsWith("exec:")) {
      updateFilters({ created_by_user_id: v.slice(5) });
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-3 w-full">
      <div className="relative">
        <Search className="absolute left-3 top-[calc(50%+2px)] h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
        <Input
          placeholder="Buscar por cliente, deal ID ou executivo"
          value={filters.search}
          onChange={(e) => updateFilters({ search: e.target.value })}
          className="pl-9 min-w-[340px] bg-white rounded-full"
        />
      </div>

      <Select value={selectValue} onValueChange={handleSelectChange}>
        <SelectTrigger className="w-[200px] bg-white h-10">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="mine">Minhas propostas</SelectItem>
          <SelectItem value="all">Todos os executivos</SelectItem>
          {executives.length > 0 && (
            <>
              <SelectSeparator />
              {executives.map((exec) => (
                <SelectItem key={exec.user_id} value={`exec:${exec.user_id}`}>
                  {exec.full_name}
                </SelectItem>
              ))}
            </>
          )}
        </SelectContent>
      </Select>

      <Button
        variant="outline"
        size="default"
        onClick={onToggleAdvanced}
        className={`gap-2 font-normal ${
          showAdvanced
            ? "bg-foreground text-background hover:bg-foreground/90 hover:text-background"
            : ""
        }`}
      >
        <SlidersHorizontal className="h-4 w-4" />
        Filtros
      </Button>

      <div className="ml-auto">
        <DateRangePicker
          dateFrom={filters.date_from}
          dateTo={filters.date_to}
          onChange={(from, to) =>
            updateFilters({ date_from: from, date_to: to })
          }
        />
      </div>
    </div>
  );
}
